import { Injectable } from '@angular/core';

import { of } from 'rxjs';
import { map, mergeMap, concatMap, catchError, withLatestFrom } from 'rxjs/operators';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store, select } from '@ngrx/store';
import { RouterReducerState } from '@ngrx/router-store';

import { RouterStateUrl } from '@sd-portal/shared/util';

import { State } from '..';
import { TripAuditService } from '../../trip-audit.service';
import * as actions from './trip.actions';

@Injectable()
export class TripEffects {
  constructor(
    private readonly tripAuditService: TripAuditService,
    private readonly actions$: Actions,
    private readonly store: Store<{
      router: RouterReducerState<RouterStateUrl>;
      sharedAudit: State;
    }>
  ) {}

  // Skeleton
  loadDeletedScheduledTripSkeletons$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadDeletedScheduledAircraftTripSkeletons),
      mergeMap(() =>
        this.tripAuditService.getDeletedScheduledTripSkeletons().pipe(
          map(s => actions.DeletedScheduledAircraftTripSkeletonsLoaded(s)),
          catchError(err => of(actions.DeletedScheduledAircraftTripSkeletonsLoadError(err)))
        )
      )
    )
  );

  loadNextPageDeletedScheduledTripSkeletons$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadNextPageDeletedScheduledAircraftTripSkeletons),
      concatMap(action => of(action).pipe(withLatestFrom(this.store.pipe(select(s => s))))),
      mergeMap(([_action, state]) => {
        const nextPageNumber = 1;
        let lastAuditLogId: number | null = null;
        if (state) {
          lastAuditLogId =
            state.sharedAudit.trip.deletedScheduledAircraftTripSkeletons.lastAuditLogId;
        }
        return this.tripAuditService
          .getDeletedScheduledTripSkeletons(nextPageNumber, undefined, lastAuditLogId)
          .pipe(
            map(s => actions.DeletedScheduledAircraftTripSkeletonsLoaded(s)),
            catchError(err => of(actions.DeletedScheduledAircraftTripSkeletonsLoadError(err)))
          );
      })
    )
  );

  loadScheduledTripSkeleton$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadScheduledAircraftTripSkeleton),
      concatMap(action => of(action).pipe(withLatestFrom(this.store.pipe(select(s => s))))),
      mergeMap(([_action, state]) => {
        const scheduledAircraftTripId = +state.router.state.params['id'];
        return this.tripAuditService.getScheduledTripSkeleton(scheduledAircraftTripId).pipe(
          map(s => actions.ScheduledAircraftTripSkeletonLoaded(s)),
          catchError(err => of(actions.ScheduledAircraftTripSkeletonLoadError(err)))
        );
      })
    )
  );

  loadScheduledTripV2Data$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadScheduledTripV2Data),
      concatMap(action => of(action).pipe(withLatestFrom(this.store.pipe(select(s => s))))),
      mergeMap(([_action, state]) => {
        const tripId = +state.router.state.params['id'];
        return [
          actions.LoadTripLogs({ tripId }),
          actions.LoadTripLegs({ tripId })
        ];
      })
    )
  );

  // ScheduledAircraftTrip
  loadScheduledAircraftTripAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadScheduledAircraftTripAudit),
      mergeMap(action =>
        this.tripAuditService.getScheduledTripAudits(action.scheduledAircraftTripId).pipe(
          map(audits =>
            actions.ScheduledAircraftTripAuditLoaded({
              scheduledAircraftTripId: action.scheduledAircraftTripId,
              audits
            })
          ),
          catchError(err => of(actions.ScheduledAircraftTripAuditLoadError(err)))
        )
      )
    )
  );

  loadNextPageScheduledAircraftTripAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadNextPageScheduledAircraftTripAudit),
      concatMap(action => of(action).pipe(withLatestFrom(this.store.pipe(select(s => s))))),
      mergeMap(([action, state]) => {
        const nextPageNumber = 1;
        let lastAuditLogId: number | null = null;
        if (state) {
          lastAuditLogId = state.sharedAudit.trip.scheduledFlightAudit.lastAuditLogId;
        }
        return this.tripAuditService
          .getScheduledTripAudits(
            action.scheduledAircraftTripId,
            nextPageNumber,
            undefined,
            lastAuditLogId
          )
          .pipe(
            map(audits =>
              actions.ScheduledAircraftTripAuditLoaded({
                scheduledAircraftTripId: action.scheduledAircraftTripId,
                audits
              })
            ),
            catchError(err => of(actions.ScheduledAircraftTripAuditLoadError(err)))
          );
      })
    )
  );

  // ScheduledFlight
  loadScheduledFlightAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadScheduledFlightAudit),
      mergeMap(action =>
        this.tripAuditService.getScheduledFlightAudits(action.scheduledFlightId).pipe(
          map(audits =>
            actions.ScheduledFlightAuditLoaded({
              scheduledFlightId: action.scheduledFlightId,
              audits
            })
          ),
          catchError(err => of(actions.ScheduledFlightAuditLoadError(err)))
        )
      )
    )
  );

  loadNextPageScheduledFlightAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadNextPageScheduledFlightAudit),
      concatMap(action => of(action).pipe(withLatestFrom(this.store.pipe(select(s => s))))),
      mergeMap(([action, state]) => {
        const nextPageNumber = 1;
        let lastAuditLogId: number | null = null;
        if (state) {
          lastAuditLogId = state.sharedAudit.trip.scheduledFlightAudit.lastAuditLogId;
        }
        return this.tripAuditService
          .getScheduledFlightAudits(
            action.scheduledFlightId,
            nextPageNumber,
            undefined,
            lastAuditLogId
          )
          .pipe(
            map(audits =>
              actions.ScheduledFlightAuditLoaded({
                scheduledFlightId: action.scheduledFlightId,
                audits
              })
            ),
            catchError(err => of(actions.ScheduledFlightAuditLoadError(err)))
          );
      })
    )
  );

  // Flight
  loadFlightAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadFlightAudit),
      mergeMap(action =>
        this.tripAuditService.getFlightAudits(action.flightId).pipe(
          map(audits => actions.FlightAuditLoaded({ flightId: action.flightId, audits })),
          catchError(err => of(actions.FlightAuditLoadError(err)))
        )
      )
    )
  );

  loadNextPageFlightAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadNextPageFlightAudit),
      concatMap(action => of(action).pipe(withLatestFrom(this.store.pipe(select(s => s))))),
      mergeMap(([action, state]) => {
        const nextPageNumber = 1;
        let lastAuditLogId: number | null = null;
        if (state) {
          lastAuditLogId = state.sharedAudit.trip.scheduledFlightAudit.lastAuditLogId;
        }
        return this.tripAuditService
          .getFlightAudits(action.flightId, nextPageNumber, undefined, lastAuditLogId)
          .pipe(
            map(audits => actions.FlightAuditLoaded({ flightId: action.flightId, audits })),
            catchError(err => of(actions.FlightAuditLoadError(err)))
          );
      })
    )
  );

  // FlightPassenger
  loadFlightPassengerAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadFlightPassengerAudit),
      mergeMap(action =>
        this.tripAuditService.getFlightPassengerAudits(action.flightPassengerId).pipe(
          map(audits =>
            actions.FlightPassengerAuditLoaded({
              flightPassengerId: action.flightPassengerId,
              audits
            })
          ),
          catchError(err => of(actions.FlightPassengerAuditLoadError(err)))
        )
      )
    )
  );

  loadNextPageFlightPassengerAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadNextPageFlightPassengerAudit),
      concatMap(action => of(action).pipe(withLatestFrom(this.store.pipe(select(s => s))))),
      mergeMap(([action, state]) => {
        const nextPageNumber = 1;
        let lastAuditLogId: number | null = null;
        if (state) {
          lastAuditLogId = state.sharedAudit.trip.flightPassengerAudit.lastAuditLogId;
        }
        return this.tripAuditService
          .getScheduledFlightAudits(
            action.flightPassengerId,
            nextPageNumber,
            undefined,
            lastAuditLogId
          )
          .pipe(
            map(audits =>
              actions.FlightPassengerAuditLoaded({
                flightPassengerId: action.flightPassengerId,
                audits
              })
            ),
            catchError(err => of(actions.FlightPassengerAuditLoadError(err)))
          );
      })
    )
  );

  // FlightPassengerCatering
  loadFlightPassengerCateringAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadFlightPassengerCateringAudit),
      mergeMap(action =>
        this.tripAuditService
          .getFlightPassengerCateringAudits(action.flightPassengerCateringId)
          .pipe(
            map(audits =>
              actions.FlightPassengerCateringAuditLoaded({
                flightPassengerCateringId: action.flightPassengerCateringId,
                audits
              })
            ),
            catchError(err => of(actions.FlightPassengerCateringAuditLoadError(err)))
          )
      )
    )
  );

  loadNextPageFlightPassengerCateringAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadNextPageFlightPassengerCateringAudit),
      concatMap(action => of(action).pipe(withLatestFrom(this.store.pipe(select(s => s))))),
      mergeMap(([action, state]) => {
        const nextPageNumber = 1;
        let lastAuditLogId: number | null = null;
        if (state) {
          lastAuditLogId = state.sharedAudit.trip.flightPassengerCateringAudit.lastAuditLogId;
        }
        return this.tripAuditService
          .getScheduledFlightAudits(
            action.flightPassengerCateringId,
            nextPageNumber,
            undefined,
            lastAuditLogId
          )
          .pipe(
            map(audits =>
              actions.FlightPassengerCateringAuditLoaded({
                flightPassengerCateringId: action.flightPassengerCateringId,
                audits
              })
            ),
            catchError(err => of(actions.FlightPassengerCateringAuditLoadError(err)))
          );
      })
    )
  );

  // FlightPassengerHotel
  loadFlightPassengerHotelAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadFlightPassengerHotelAudit),
      mergeMap(action =>
        this.tripAuditService.getFlightPassengerHotelAudits(action.flightPassengerHotelId).pipe(
          map(audits =>
            actions.FlightPassengerHotelAuditLoaded({
              flightPassengerHotelId: action.flightPassengerHotelId,
              audits
            })
          ),
          catchError(err => of(actions.FlightPassengerHotelAuditLoadError(err)))
        )
      )
    )
  );

  loadNextPageFlightPassengerHotelAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadNextPageFlightPassengerHotelAudit),
      concatMap(action => of(action).pipe(withLatestFrom(this.store.pipe(select(s => s))))),
      mergeMap(([action, state]) => {
        const nextPageNumber = 1;
        let lastAuditLogId: number | null = null;
        if (state) {
          lastAuditLogId = state.sharedAudit.trip.flightPassengerHotelAudit.lastAuditLogId;
        }
        return this.tripAuditService
          .getScheduledFlightAudits(
            action.flightPassengerHotelId,
            nextPageNumber,
            undefined,
            lastAuditLogId
          )
          .pipe(
            map(audits =>
              actions.FlightPassengerHotelAuditLoaded({
                flightPassengerHotelId: action.flightPassengerHotelId,
                audits
              })
            ),
            catchError(err => of(actions.FlightPassengerHotelAuditLoadError(err)))
          );
      })
    )
  );

  // FlightPassengerTransportation
  loadFlightPassengerTransportationAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadFlightPassengerTransportationAudit),
      mergeMap(action =>
        this.tripAuditService
          .getFlightPassengerTransportationAudits(action.flightPassengerTransportationId)
          .pipe(
            map(audits =>
              actions.FlightPassengerTransportationAuditLoaded({
                flightPassengerTransportationId: action.flightPassengerTransportationId,
                audits
              })
            ),
            catchError(err => of(actions.FlightPassengerTransportationAuditLoadError(err)))
          )
      )
    )
  );

  loadNextPageFlightPassengerTransportationAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadNextPageFlightPassengerTransportationAudit),
      concatMap(action => of(action).pipe(withLatestFrom(this.store.pipe(select(s => s))))),
      mergeMap(([action, state]) => {
        const nextPageNumber = 1;
        let lastAuditLogId: number | null = null;
        if (state) {
          lastAuditLogId = state.sharedAudit.trip.flightPassengerTransportationAudit.lastAuditLogId;
        }
        return this.tripAuditService
          .getScheduledFlightAudits(
            action.flightPassengerTransportationId,
            nextPageNumber,
            undefined,
            lastAuditLogId
          )
          .pipe(
            map(audits =>
              actions.FlightPassengerTransportationAuditLoaded({
                flightPassengerTransportationId: action.flightPassengerTransportationId,
                audits
              })
            ),
            catchError(err => of(actions.FlightPassengerTransportationAuditLoadError(err)))
          );
      })
    )
  );

  // FlightCrewMember
  loadFlightCrewMemberAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadFlightCrewMemberAudit),
      mergeMap(action =>
        this.tripAuditService.getFlightCrewMemberAudits(action.flightCrewMemberId).pipe(
          map(audits =>
            actions.FlightCrewMemberAuditLoaded({
              flightCrewMemberId: action.flightCrewMemberId,
              audits
            })
          ),
          catchError(err => of(actions.FlightCrewMemberAuditLoadError(err)))
        )
      )
    )
  );

  loadNextPageFlightCrewMemberAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadNextPageFlightCrewMemberAudit),
      concatMap(action => of(action).pipe(withLatestFrom(this.store.pipe(select(s => s))))),
      mergeMap(([action, state]) => {
        const nextPageNumber = 1;
        let lastAuditLogId: number | null = null;
        if (state) {
          lastAuditLogId = state.sharedAudit.trip.flightCrewMemberAudit.lastAuditLogId;
        }
        return this.tripAuditService
          .getFlightCrewMemberAudits(
            action.flightCrewMemberId,
            nextPageNumber,
            undefined,
            lastAuditLogId
          )
          .pipe(
            map(audits =>
              actions.FlightCrewMemberAuditLoaded({
                flightCrewMemberId: action.flightCrewMemberId,
                audits
              })
            ),
            catchError(err => of(actions.FlightCrewMemberAuditLoadError(err)))
          );
      })
    )
  );

  // FlightCrewMemberCatering
  loadFlightCrewMemberCateringAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadFlightCrewMemberCateringAudit),
      mergeMap(action =>
        this.tripAuditService
          .getFlightCrewMemberCateringAudits(action.flightCrewMemberCateringId)
          .pipe(
            map(audits =>
              actions.FlightCrewMemberCateringAuditLoaded({
                flightCrewMemberCateringId: action.flightCrewMemberCateringId,
                audits
              })
            ),
            catchError(err => of(actions.FlightCrewMemberCateringAuditLoadError(err)))
          )
      )
    )
  );

  loadNextPageFlightCrewMemberCateringAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadNextPageFlightCrewMemberCateringAudit),
      concatMap(action => of(action).pipe(withLatestFrom(this.store.pipe(select(s => s))))),
      mergeMap(([action, state]) => {
        const nextPageNumber = 1;
        let lastAuditLogId: number | null = null;
        if (state) {
          lastAuditLogId = state.sharedAudit.trip.flightCrewMemberCateringAudit.lastAuditLogId;
        }
        return this.tripAuditService
          .getScheduledFlightAudits(
            action.flightCrewMemberCateringId,
            nextPageNumber,
            undefined,
            lastAuditLogId
          )
          .pipe(
            map(audits =>
              actions.FlightCrewMemberCateringAuditLoaded({
                flightCrewMemberCateringId: action.flightCrewMemberCateringId,
                audits
              })
            ),
            catchError(err => of(actions.FlightCrewMemberCateringAuditLoadError(err)))
          );
      })
    )
  );

  // FlightCrewMemberHotel
  loadFlightCrewMemberHotelAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadFlightCrewMemberHotelAudit),
      mergeMap(action =>
        this.tripAuditService.getFlightCrewMemberHotelAudits(action.flightCrewMemberHotelId).pipe(
          map(audits =>
            actions.FlightCrewMemberHotelAuditLoaded({
              flightCrewMemberHotelId: action.flightCrewMemberHotelId,
              audits
            })
          ),
          catchError(err => of(actions.FlightCrewMemberHotelAuditLoadError(err)))
        )
      )
    )
  );

  loadNextPageFlightCrewMemberHotelAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadNextPageFlightCrewMemberHotelAudit),
      concatMap(action => of(action).pipe(withLatestFrom(this.store.pipe(select(s => s))))),
      mergeMap(([action, state]) => {
        const nextPageNumber = 1;
        let lastAuditLogId: number | null = null;
        if (state) {
          lastAuditLogId = state.sharedAudit.trip.flightCrewMemberHotelAudit.lastAuditLogId;
        }
        return this.tripAuditService
          .getScheduledFlightAudits(
            action.flightCrewMemberHotelId,
            nextPageNumber,
            undefined,
            lastAuditLogId
          )
          .pipe(
            map(audits =>
              actions.FlightCrewMemberHotelAuditLoaded({
                flightCrewMemberHotelId: action.flightCrewMemberHotelId,
                audits
              })
            ),
            catchError(err => of(actions.FlightCrewMemberHotelAuditLoadError(err)))
          );
      })
    )
  );

  // FlightCrewMemberTransportation
  loadFlightCrewMemberTransportationAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadFlightCrewMemberTransportationAudit),
      mergeMap(action =>
        this.tripAuditService
          .getFlightCrewMemberTransportationAudits(action.flightCrewMemberTransportationId)
          .pipe(
            map(audits =>
              actions.FlightCrewMemberTransportationAuditLoaded({
                flightCrewMemberTransportationId: action.flightCrewMemberTransportationId,
                audits
              })
            ),
            catchError(err => of(actions.FlightCrewMemberTransportationAuditLoadError(err)))
          )
      )
    )
  );

  loadNextPageFlightCrewMemberTransportationAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadNextPageFlightCrewMemberTransportationAudit),
      concatMap(action => of(action).pipe(withLatestFrom(this.store.pipe(select(s => s))))),
      mergeMap(([action, state]) => {
        const nextPageNumber = 1;
        let lastAuditLogId: number | null = null;
        if (state) {
          lastAuditLogId =
            state.sharedAudit.trip.flightCrewMemberTransportationAudit.lastAuditLogId;
        }
        return this.tripAuditService
          .getScheduledFlightAudits(
            action.flightCrewMemberTransportationId,
            nextPageNumber,
            undefined,
            lastAuditLogId
          )
          .pipe(
            map(audits =>
              actions.FlightCrewMemberTransportationAuditLoaded({
                flightCrewMemberTransportationId: action.flightCrewMemberTransportationId,
                audits
              })
            ),
            catchError(err => of(actions.FlightCrewMemberTransportationAuditLoadError(err)))
          );
      })
    )
  );

  // FlightBusinessPerson
  loadFlightBusinessPersonAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadFlightBusinessPersonAudit),
      mergeMap(action =>
        this.tripAuditService.getFlightBusinessPersonAudits(action.flightBusinessPersonId).pipe(
          map(audits =>
            actions.FlightBusinessPersonAuditLoaded({
              flightBusinessPersonId: action.flightBusinessPersonId,
              audits
            })
          ),
          catchError(err => of(actions.FlightBusinessPersonAuditLoadError(err)))
        )
      )
    )
  );

  loadNextPageFlightBusinessPersonAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadNextPageFlightBusinessPersonAudit),
      concatMap(action => of(action).pipe(withLatestFrom(this.store.pipe(select(s => s))))),
      mergeMap(([action, state]) => {
        const nextPageNumber = 1;
        let lastAuditLogId: number | null = null;
        if (state) {
          lastAuditLogId = state.sharedAudit.trip.flightBusinessPersonAudit.lastAuditLogId;
        }
        return this.tripAuditService
          .getFlightBusinessPersonAudits(
            action.flightBusinessPersonId,
            nextPageNumber,
            undefined,
            lastAuditLogId
          )
          .pipe(
            map(audits =>
              actions.FlightBusinessPersonAuditLoaded({
                flightBusinessPersonId: action.flightBusinessPersonId,
                audits
              })
            ),
            catchError(err => of(actions.FlightBusinessPersonAuditLoadError(err)))
          );
      })
    )
  );

  // ScheduledEvent
  loadScheduledEventAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadScheduledEventAudit),
      mergeMap(action =>
        this.tripAuditService.getScheduledEventAudits(action.scheduledEventId).pipe(
          map(audits =>
            actions.ScheduledEventAuditLoaded({
              scheduledEventId: action.scheduledEventId,
              audits
            })
          ),
          catchError(err => of(actions.ScheduledEventAuditLoadError(err)))
        )
      )
    )
  );

  loadNextPageScheduledEventAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadNextPageScheduledEventAudit),
      concatMap(action => of(action).pipe(withLatestFrom(this.store.pipe(select(s => s))))),
      mergeMap(([action, state]) => {
        const nextPageNumber = 1;
        let lastAuditLogId: number | null = null;
        if (state) {
          lastAuditLogId = state.sharedAudit.trip.scheduledEventAudit.lastAuditLogId;
        }
        return this.tripAuditService
          .getScheduledEventAudits(
            action.scheduledEventId,
            nextPageNumber,
            undefined,
            lastAuditLogId
          )
          .pipe(
            map(audits =>
              actions.ScheduledEventAuditLoaded({
                scheduledEventId: action.scheduledEventId,
                audits
              })
            ),
            catchError(err => of(actions.ScheduledEventAuditLoadError(err)))
          );
      })
    )
  );

  //LegNote
  loadLegNoteAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadLegNoteAudit),
      mergeMap(action =>
        this.tripAuditService.getLegNoteAudits(action.legNoteId).pipe(
          map(audits =>
            actions.LegNoteAuditLoaded({
              legNoteId: action.legNoteId,
              audits
            })
          ),
          catchError(err => of(actions.LegNoteAuditLoadError(err)))
        )
      )
    )
  );

  loadNextPageLegNoteAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadNextPageLegNoteAudit),
      concatMap(action => of(action).pipe(withLatestFrom(this.store.pipe(select(s => s))))),
      mergeMap(([action, state]) => {
        const nextPageNumber = 1;
        let lastAuditLogId: number | null = null;
        if (state) {
          lastAuditLogId = state.sharedAudit.trip.legNoteAudit.lastAuditLogId;
        }
        return this.tripAuditService
          .getLegNoteAudits(action.legNoteId, nextPageNumber, undefined, lastAuditLogId)
          .pipe(
            map(audits =>
              actions.LegNoteAuditLoaded({
                legNoteId: action.legNoteId,
                audits
              })
            ),
            catchError(err => of(actions.LegNoteAuditLoadError(err)))
          );
      })
    )
  );

  // FlightFuel
  loadFlightFuelAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadFlightFuelAudit),
      mergeMap(action =>
        this.tripAuditService.getFlightFuelAudits(action.flightFuelId).pipe(
          map(audits =>
            actions.FlightFuelAuditLoaded({
              flightFuelId: action.flightFuelId,
              audits
            })
          ),
          catchError(err => of(actions.FlightFuelAuditLoadError(err)))
        )
      )
    )
  );

  loadNextPageFlightFuelAudit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadNextPageFlightFuelAudit),
      concatMap(action => of(action).pipe(withLatestFrom(this.store.pipe(select(s => s))))),
      mergeMap(([action, state]) => {
        const nextPageNumber = 1;
        let lastAuditLogId: number | null = null;
        if (state) {
          lastAuditLogId = state.sharedAudit.trip.flightFuelAudit.lastAuditLogId;
        }
        return this.tripAuditService
          .getScheduledFlightAudits(action.flightFuelId, nextPageNumber, undefined, lastAuditLogId)
          .pipe(
            map(audits =>
              actions.FlightFuelAuditLoaded({
                flightFuelId: action.flightFuelId,
                audits
              })
            ),
            catchError(err => of(actions.FlightFuelAuditLoadError(err)))
          );
      })
    )
  );

  // V2 Effects for lazy loading

  // Deleted Trips
  loadDeletedTrips$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadDeletedTrips),
      mergeMap(() =>
        this.tripAuditService.getDeletedTrips().pipe(
          map(response => actions.DeletedTripsLoaded(response)),
          catchError(err => of(actions.DeletedTripsLoadError({ err })))
        )
      )
    )
  );

  nextDeletedTripsPage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadNextPageDeletedTrips),
      concatMap(action => of(action).pipe(withLatestFrom(this.store.pipe(select(s => s))))),
      mergeMap(([_action, state]) => {
        const nextPageNumber = state.sharedAudit.trip.deletedTrips.pageNumber + 1;
        return this.tripAuditService.getDeletedTrips(nextPageNumber).pipe(
          map(response => actions.DeletedTripsLoaded(response)),
          catchError(err => of(actions.DeletedTripsLoadError({ err })))
        );
      })
    )
  );

  // Trip Logs
  loadTripLogs$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadTripLogs),
      mergeMap(action =>
        this.tripAuditService.getTripLogs(action.tripId).pipe(
          map(logs => actions.TripLogsLoaded({ tripId: action.tripId, logs })),
          catchError(err => of(actions.TripLogsLoadError({ err })))
        )
      )
    )
  );

  nextTripLogsPage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadNextPageTripLogs),
      concatMap(action => of(action).pipe(withLatestFrom(this.store.pipe(select(s => s))))),
      mergeMap(([action, state]) => {
        const nextPageNumber = 1;
        let lastId: number | undefined;
        if (state) {
          lastId = state.sharedAudit.trip.tripLogs.lastId ?? undefined;
        }
        return this.tripAuditService.getTripLogs(action.tripId, nextPageNumber, undefined, lastId).pipe(
          map(logs => actions.TripLogsLoaded({ tripId: action.tripId, logs })),
          catchError(err => of(actions.TripLogsLoadError({ err })))
        );
      })
    )
  );

  // Deleted Trip Legs
  loadDeletedTripLegs$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadDeletedTripLegs),
      mergeMap(action =>
        this.tripAuditService.getDeletedTripLegs(action.tripId).pipe(
          map(legs => actions.DeletedTripLegsLoaded({ tripId: action.tripId, legs })),
          catchError(err => of(actions.DeletedTripLegsLoadError({ err })))
        )
      )
    )
  );

  nextDeletedTripLegsPage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadNextPageDeletedTripLegs),
      concatMap(action => of(action).pipe(withLatestFrom(this.store.pipe(select(s => s))))),
      mergeMap(([action, state]) => {
        const nextPageNumber = state.sharedAudit.trip.deletedTripLegs.pageNumber + 1;
        return this.tripAuditService.getDeletedTripLegs(action.tripId, nextPageNumber).pipe(
          map(legs => actions.DeletedTripLegsLoaded({ tripId: action.tripId, legs })),
          catchError(err => of(actions.DeletedTripLegsLoadError({ err })))
        );
      })
    )
  );

  // Trip Legs
  loadTripLegs$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadTripLegs),
      mergeMap(action =>
        this.tripAuditService.getTripLegs(action.tripId).pipe(
          map(legs => actions.TripLegsLoaded({ tripId: action.tripId, legs })),
          catchError(err => of(actions.TripLegsLoadError({ err })))
        )
      )
    )
  );

  nextTripLegsPage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadNextPageTripLegs),
      concatMap(action => of(action).pipe(withLatestFrom(this.store.pipe(select(s => s))))),
      mergeMap(([action, state]) => {
        const nextPageNumber = state.sharedAudit.trip.tripLegs.pageNumber + 1;
        return this.tripAuditService.getTripLegs(action.tripId, nextPageNumber).pipe(
          map(legs => actions.TripLegsLoaded({ tripId: action.tripId, legs })),
          catchError(err => of(actions.TripLegsLoadError({ err })))
        );
      })
    )
  );

  // Leg Event Logs (Dep/Arr Time)
  loadLegEventLogs$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadLegEventLogs),
      mergeMap(action =>
        this.tripAuditService.getEventLogs(action.eventId).pipe(
          map(logs => actions.LegEventLogsLoaded({ eventId: action.eventId, logs })),
          catchError(err => of(actions.LegEventLogsLoadError({ err })))
        )
      )
    )
  );

  nextLegEventLogsPage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadNextPageLegEventLogs),
      concatMap(action => of(action).pipe(withLatestFrom(this.store.pipe(select(s => s))))),
      mergeMap(([action, state]) => {
        const nextPageNumber = 1;
        let lastId: number | undefined;
        if (state) {
          lastId = state.sharedAudit.trip.legEventLogs.lastId ?? undefined;
        }
        return this.tripAuditService.getEventLogs(action.eventId, nextPageNumber, undefined, lastId).pipe(
          map(logs => actions.LegEventLogsLoaded({ eventId: action.eventId, logs })),
          catchError(err => of(actions.LegEventLogsLoadError({ err })))
        );
      })
    )
  );

  // Leg Flight Logs (Details)
  loadLegFlightLogs$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadLegFlightLogs),
      mergeMap(action =>
        this.tripAuditService.getFlightLogs(action.flightId).pipe(
          map(logs => actions.LegFlightLogsLoaded({ flightId: action.flightId, logs })),
          catchError(err => of(actions.LegFlightLogsLoadError({ err })))
        )
      )
    )
  );

  nextLegFlightLogsPage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadNextPageLegFlightLogs),
      concatMap(action => of(action).pipe(withLatestFrom(this.store.pipe(select(s => s))))),
      mergeMap(([action, state]) => {
        const nextPageNumber = 1;
        let lastId: number | undefined;
        if (state) {
          lastId = state.sharedAudit.trip.legFlightLogs.lastId ?? undefined;
        }
        return this.tripAuditService.getFlightLogs(action.flightId, nextPageNumber, undefined, lastId).pipe(
          map(logs => actions.LegFlightLogsLoaded({ flightId: action.flightId, logs })),
          catchError(err => of(actions.LegFlightLogsLoadError({ err })))
        );
      })
    )
  );

  // Leg Fuel Logs
  loadLegFuelLogs$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadLegFuelLogs),
      mergeMap(action =>
        this.tripAuditService.getFuelLogs(action.fuelId).pipe(
          map(logs => actions.LegFuelLogsLoaded({ fuelId: action.fuelId, logs })),
          catchError(err => of(actions.LegFuelLogsLoadError({ err })))
        )
      )
    )
  );

  nextLegFuelLogsPage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadNextPageLegFuelLogs),
      concatMap(action => of(action).pipe(withLatestFrom(this.store.pipe(select(s => s))))),
      mergeMap(([action, state]) => {
        const nextPageNumber = 1;
        let lastId: number | undefined;
        if (state) {
          lastId = state.sharedAudit.trip.legFuelLogs.lastId ?? undefined;
        }
        return this.tripAuditService.getFuelLogs(action.fuelId, nextPageNumber, undefined, lastId).pipe(
          map(logs => actions.LegFuelLogsLoaded({ fuelId: action.fuelId, logs })),
          catchError(err => of(actions.LegFuelLogsLoadError({ err })))
        );
      })
    )
  );

  // Flight Crew Members
  loadFlightCrewMembers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadFlightCrewMembers),
      mergeMap(action =>
        this.tripAuditService.getFlightCrewMembers(action.flightId, 1).pipe(
          map(data => actions.FlightCrewMembersLoaded({ flightId: action.flightId, data })),
          catchError(err => of(actions.FlightCrewMembersLoadError({ err })))
        )
      )
    )
  );

  nextFlightCrewMembersPage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadNextPageFlightCrewMembers),
      withLatestFrom(this.store),
      mergeMap(([action, state]) => {
        const nextPageNumber = (state.sharedAudit.trip.flightCrewMembers.pageNumber || 0) + 1;
        return this.tripAuditService.getFlightCrewMembers(action.flightId, nextPageNumber).pipe(
          map(data => actions.FlightCrewMembersLoaded({ flightId: action.flightId, data })),
          catchError(err => of(actions.FlightCrewMembersLoadError({ err })))
        );
      })
    )
  );

  loadScheduledFlightCrewMembers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadScheduledFlightCrewMembers),
      mergeMap(action =>
        this.tripAuditService.getScheduledFlightCrewMembers(action.flightId, 1).pipe(
          map(data => actions.FlightCrewMembersLoaded({ flightId: action.flightId, data })),
          catchError(err => of(actions.FlightCrewMembersLoadError({ err })))
        )
      )
    )
  );

  nextScheduledFlightCrewMembersPage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadNextPageScheduledFlightCrewMembers),
      withLatestFrom(this.store),
      mergeMap(([action, state]) => {
        const nextPageNumber = (state.sharedAudit.trip.flightCrewMembers.pageNumber || 0) + 1;
        return this.tripAuditService
          .getScheduledFlightCrewMembers(action.flightId, nextPageNumber)
          .pipe(
            map(data => actions.FlightCrewMembersLoaded({ flightId: action.flightId, data })),
            catchError(err => of(actions.FlightCrewMembersLoadError({ err })))
          );
      })
    )
  );

  // Crew Logs
  loadCrewLogs$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadCrewLogs),
      mergeMap(action =>
        this.tripAuditService.getCrewLogs(action.crewId).pipe(
          map(logs => actions.CrewLogsLoaded({ crewId: action.crewId, logs })),
          catchError(err => of(actions.CrewLogsLoadError({ err })))
        )
      )
    )
  );

  nextCrewLogsPage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadNextPageCrewLogs),
      concatMap(action => of(action).pipe(withLatestFrom(this.store.pipe(select(s => s))))),
      mergeMap(([action, state]) => {
        const nextPageNumber = 1;
        let lastId: number | undefined;
        if (state) {
          lastId = state.sharedAudit.trip.crewLogs.lastId ?? undefined;
        }
        return this.tripAuditService.getCrewLogs(action.crewId, nextPageNumber, undefined, lastId).pipe(
          map(logs => actions.CrewLogsLoaded({ crewId: action.crewId, logs })),
          catchError(err => of(actions.CrewLogsLoadError({ err })))
        );
      })
    )
  );

  // Crew Catering Logs
  loadCrewCateringLogs$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadCrewCateringLogs),
      mergeMap(action =>
        this.tripAuditService.getCrewCateringLogs(action.cateringId).pipe(
          map(logs => actions.CrewCateringLogsLoaded({ cateringId: action.cateringId, logs })),
          catchError(err => of(actions.CrewCateringLogsLoadError({ err })))
        )
      )
    )
  );

  nextCrewCateringLogsPage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadNextPageCrewCateringLogs),
      concatMap(action => of(action).pipe(withLatestFrom(this.store.pipe(select(s => s))))),
      mergeMap(([action, state]) => {
        const nextPageNumber = 1;
        let lastId: number | undefined;
        if (state) {
          lastId = state.sharedAudit.trip.crewCateringLogs.lastId ?? undefined;
        }
        return this.tripAuditService.getCrewCateringLogs(action.cateringId, nextPageNumber, undefined, lastId).pipe(
          map(logs => actions.CrewCateringLogsLoaded({ cateringId: action.cateringId, logs })),
          catchError(err => of(actions.CrewCateringLogsLoadError({ err })))
        );
      })
    )
  );

  // Crew Hotel Logs
  loadCrewHotelLogs$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadCrewHotelLogs),
      mergeMap(action =>
        this.tripAuditService.getCrewHotelLogs(action.hotelId).pipe(
          map(logs => actions.CrewHotelLogsLoaded({ hotelId: action.hotelId, logs })),
          catchError(err => of(actions.CrewHotelLogsLoadError({ err })))
        )
      )
    )
  );

  nextCrewHotelLogsPage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadNextPageCrewHotelLogs),
      concatMap(action => of(action).pipe(withLatestFrom(this.store.pipe(select(s => s))))),
      mergeMap(([action, state]) => {
        const nextPageNumber = 1;
        let lastId: number | undefined;
        if (state) {
          lastId = state.sharedAudit.trip.crewHotelLogs.lastId ?? undefined;
        }
        return this.tripAuditService.getCrewHotelLogs(action.hotelId, nextPageNumber, undefined, lastId).pipe(
          map(logs => actions.CrewHotelLogsLoaded({ hotelId: action.hotelId, logs })),
          catchError(err => of(actions.CrewHotelLogsLoadError({ err })))
        );
      })
    )
  );

  // Crew Transportation Logs
  loadCrewTransportationLogs$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadCrewTransportationLogs),
      mergeMap(action =>
        this.tripAuditService.getCrewTransportationLogs(action.transportationId).pipe(
          map(logs => actions.CrewTransportationLogsLoaded({ transportationId: action.transportationId, logs })),
          catchError(err => of(actions.CrewTransportationLogsLoadError({ err })))
        )
      )
    )
  );

  nextCrewTransportationLogsPage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadNextPageCrewTransportationLogs),
      concatMap(action => of(action).pipe(withLatestFrom(this.store.pipe(select(s => s))))),
      mergeMap(([action, state]) => {
        const nextPageNumber = 1;
        let lastId: number | undefined;
        if (state) {
          lastId = state.sharedAudit.trip.crewTransportationLogs.lastId ?? undefined;
        }
        return this.tripAuditService.getCrewTransportationLogs(action.transportationId, nextPageNumber, undefined, lastId).pipe(
          map(logs => actions.CrewTransportationLogsLoaded({ transportationId: action.transportationId, logs })),
          catchError(err => of(actions.CrewTransportationLogsLoadError({ err })))
        );
      })
    )
  );

  // Flight Passengers
  loadFlightPassengers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadFlightPassengers),
      mergeMap(action =>
        this.tripAuditService.getFlightPassengers(action.flightId, 1).pipe(
          map(data => actions.FlightPassengersLoaded({ flightId: action.flightId, data })),
          catchError(err => of(actions.FlightPassengersLoadError({ err })))
        )
      )
    )
  );

  nextFlightPassengersPage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadNextPageFlightPassengers),
      withLatestFrom(this.store),
      mergeMap(([action, state]) => {
        const nextPageNumber = (state.sharedAudit.trip.flightPassengers.pageNumber || 0) + 1;
        return this.tripAuditService.getFlightPassengers(action.flightId, nextPageNumber).pipe(
          map(data => actions.FlightPassengersLoaded({ flightId: action.flightId, data })),
          catchError(err => of(actions.FlightPassengersLoadError({ err })))
        );
      })
    )
  );

  loadScheduledFlightPassengers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadScheduledFlightPassengers),
      mergeMap(action =>
        this.tripAuditService.getScheduledFlightPassengers(action.flightId, 1).pipe(
          map(data => actions.FlightPassengersLoaded({ flightId: action.flightId, data })),
          catchError(err => of(actions.FlightPassengersLoadError({ err })))
        )
      )
    )
  );

  nextScheduledFlightPassengersPage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadNextPageScheduledFlightPassengers),
      withLatestFrom(this.store),
      mergeMap(([action, state]) => {
        const nextPageNumber = (state.sharedAudit.trip.flightPassengers.pageNumber || 0) + 1;
        return this.tripAuditService
          .getScheduledFlightPassengers(action.flightId, nextPageNumber)
          .pipe(
            map(data => actions.FlightPassengersLoaded({ flightId: action.flightId, data })),
            catchError(err => of(actions.FlightPassengersLoadError({ err })))
          );
      })
    )
  );

  // Passenger Logs
  loadPassengerLogs$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadPassengerLogs),
      mergeMap(action =>
        this.tripAuditService.getPassengerLogs(action.paxId).pipe(
          map(logs => actions.PassengerLogsLoaded({ paxId: action.paxId, logs })),
          catchError(err => of(actions.PassengerLogsLoadError({ err })))
        )
      )
    )
  );

  nextPassengerLogsPage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadNextPagePassengerLogs),
      concatMap(action => of(action).pipe(withLatestFrom(this.store.pipe(select(s => s))))),
      mergeMap(([action, state]) => {
        const nextPageNumber = 1;
        let lastId: number | undefined;
        if (state) {
          lastId = state.sharedAudit.trip.passengerLogs.lastId ?? undefined;
        }
        return this.tripAuditService.getPassengerLogs(action.paxId, nextPageNumber, undefined, lastId).pipe(
          map(logs => actions.PassengerLogsLoaded({ paxId: action.paxId, logs })),
          catchError(err => of(actions.PassengerLogsLoadError({ err })))
        );
      })
    )
  );

  // Passenger Catering Logs
  loadPassengerCateringLogs$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadPassengerCateringLogs),
      mergeMap(action =>
        this.tripAuditService.getPassengerCateringLogs(action.cateringId).pipe(
          map(logs => actions.PassengerCateringLogsLoaded({ cateringId: action.cateringId, logs })),
          catchError(err => of(actions.PassengerCateringLogsLoadError({ err })))
        )
      )
    )
  );

  nextPassengerCateringLogsPage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadNextPagePassengerCateringLogs),
      concatMap(action => of(action).pipe(withLatestFrom(this.store.pipe(select(s => s))))),
      mergeMap(([action, state]) => {
        const nextPageNumber = 1;
        let lastId: number | undefined;
        if (state) {
          lastId = state.sharedAudit.trip.passengerCateringLogs.lastId ?? undefined;
        }
        return this.tripAuditService.getPassengerCateringLogs(action.cateringId, nextPageNumber, undefined, lastId).pipe(
          map(logs => actions.PassengerCateringLogsLoaded({ cateringId: action.cateringId, logs })),
          catchError(err => of(actions.PassengerCateringLogsLoadError({ err })))
        );
      })
    )
  );

  // Passenger Hotel Logs
  loadPassengerHotelLogs$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadPassengerHotelLogs),
      mergeMap(action =>
        this.tripAuditService.getPassengerHotelLogs(action.hotelId).pipe(
          map(logs => actions.PassengerHotelLogsLoaded({ hotelId: action.hotelId, logs })),
          catchError(err => of(actions.PassengerHotelLogsLoadError({ err })))
        )
      )
    )
  );

  nextPassengerHotelLogsPage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadNextPagePassengerHotelLogs),
      concatMap(action => of(action).pipe(withLatestFrom(this.store.pipe(select(s => s))))),
      mergeMap(([action, state]) => {
        const nextPageNumber = 1;
        let lastId: number | undefined;
        if (state) {
          lastId = state.sharedAudit.trip.passengerHotelLogs.lastId ?? undefined;
        }
        return this.tripAuditService.getPassengerHotelLogs(action.hotelId, nextPageNumber, undefined, lastId).pipe(
          map(logs => actions.PassengerHotelLogsLoaded({ hotelId: action.hotelId, logs })),
          catchError(err => of(actions.PassengerHotelLogsLoadError({ err })))
        );
      })
    )
  );

  // Passenger Transportation Logs
  loadPassengerTransportationLogs$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadPassengerTransportationLogs),
      mergeMap(action =>
        this.tripAuditService.getPassengerTransportationLogs(action.transportationId).pipe(
          map(logs => actions.PassengerTransportationLogsLoaded({ transportationId: action.transportationId, logs })),
          catchError(err => of(actions.PassengerTransportationLogsLoadError({ err })))
        )
      )
    )
  );

  nextPassengerTransportationLogsPage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadNextPagePassengerTransportationLogs),
      concatMap(action => of(action).pipe(withLatestFrom(this.store.pipe(select(s => s))))),
      mergeMap(([action, state]) => {
        const nextPageNumber = 1;
        let lastId: number | undefined;
        if (state) {
          lastId = state.sharedAudit.trip.passengerTransportationLogs.lastId ?? undefined;
        }
        return this.tripAuditService.getPassengerTransportationLogs(action.transportationId, nextPageNumber, undefined, lastId).pipe(
          map(logs => actions.PassengerTransportationLogsLoaded({ transportationId: action.transportationId, logs })),
          catchError(err => of(actions.PassengerTransportationLogsLoadError({ err })))
        );
      })
    )
  );

  // --- Tier-1: Flight Schedulers ---
  loadFlightSchedulers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadFlightSchedulers),
      mergeMap(action =>
        this.tripAuditService.getFlightSchedulers(action.flightId, 1).pipe(
          map(data => actions.FlightSchedulersLoaded({ flightId: action.flightId, data })),
          catchError(err => of(actions.FlightSchedulersLoadError({ err })))
        )
      )
    )
  );

  nextFlightSchedulersPage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadNextPageFlightSchedulers),
      withLatestFrom(this.store),
      mergeMap(([action, state]) => {
        const nextPageNumber = (state.sharedAudit.trip.flightSchedulers.pageNumber || 0) + 1;
        return this.tripAuditService.getFlightSchedulers(action.flightId, nextPageNumber).pipe(
          map(data => actions.FlightSchedulersLoaded({ flightId: action.flightId, data })),
          catchError(err => of(actions.FlightSchedulersLoadError({ err })))
        );
      })
    )
  );

  loadScheduledFlightSchedulers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadScheduledFlightSchedulers),
      mergeMap(action =>
        this.tripAuditService.getScheduledFlightSchedulers(action.flightId, 1).pipe(
          map(data => actions.FlightSchedulersLoaded({ flightId: action.flightId, data })),
          catchError(err => of(actions.FlightSchedulersLoadError({ err })))
        )
      )
    )
  );

  nextScheduledFlightSchedulersPage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadNextPageScheduledFlightSchedulers),
      withLatestFrom(this.store),
      mergeMap(([action, state]) => {
        const nextPageNumber = (state.sharedAudit.trip.flightSchedulers.pageNumber || 0) + 1;
        return this.tripAuditService.getScheduledFlightSchedulers(action.flightId, nextPageNumber).pipe(
          map(data => actions.FlightSchedulersLoaded({ flightId: action.flightId, data })),
          catchError(err => of(actions.FlightSchedulersLoadError({ err })))
        );
      })
    )
  );

  // --- Tier-2: Scheduler Logs ---
  loadSchedulerLogs$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadSchedulerLogs),
      mergeMap(action =>
        this.tripAuditService.getSchedulerLogs(action.schedulerId).pipe(
          map(logs => actions.SchedulerLogsLoaded({ schedulerId: action.schedulerId, logs })),
          catchError(err => of(actions.SchedulerLogsLoadError({ err })))
        )
      )
    )
  );

  nextSchedulerLogsPage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.LoadNextPageSchedulerLogs),
      concatMap(action => of(action).pipe(withLatestFrom(this.store.pipe(select(s => s))))),
      mergeMap(([action, state]) => {
        const nextPageNumber = 1;
        let lastId: number | undefined;
        if (state) {
          lastId = state.sharedAudit.trip.schedulerLogs.lastId ?? undefined;
        }
        return this.tripAuditService.getSchedulerLogs(action.schedulerId, nextPageNumber, undefined, lastId).pipe(
          map(logs => actions.SchedulerLogsLoaded({ schedulerId: action.schedulerId, logs })),
          catchError(err => of(actions.SchedulerLogsLoadError({ err })))
        );
      })
    )
  );
}
